import React from 'react';

import { useTranslation } from '@modernloop/i18n';
import { Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import { TRAINEE_SCHEDULING_STRATEGY, TraineeSchedulingStrategy } from 'src/store/slices/job-stage-interview-seat';

type SelectionOption = {
  key: TraineeSchedulingStrategy;
  label: string;
};

type Props = {
  schedulingStrategy: TraineeSchedulingStrategy;
  onStrategyChange: (strategy: TraineeSchedulingStrategy) => void;
};

/**
 * Best-effort vs required scheduling control shown when at least one trainee role row is enabled.
 */
const TraineeRequirementRow = ({ schedulingStrategy, onStrategyChange }: Props): JSX.Element => {
  const { t } = useTranslation();

  const options: SelectionOption[] = [
    {
      key: TRAINEE_SCHEDULING_STRATEGY.IS_AVAILABLE,
      label: t('hiring-dashboard-filter:filter.traineeRequirement.bestEffort'),
    },
    {
      key: TRAINEE_SCHEDULING_STRATEGY.REQUIRED,
      label: t('hiring-dashboard-filter:filter.traineeRequirement.required'),
    },
  ];

  const handleStrategyChange = (_: React.MouseEvent<HTMLElement>, value: TraineeSchedulingStrategy | null) => {
    if (value !== null) {
      onStrategyChange(value);
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
      sx={(theme) => ({
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '6px',
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(1, 1.25),
      })}
    >
      <Typography variant="body2" fontWeight={600} color="text.primary" lineHeight={1.3} sx={{ minWidth: 0 }}>
        {t('hiring-dashboard-filter:filter.traineeRequirementLabel')}
      </Typography>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={schedulingStrategy}
        onChange={handleStrategyChange}
        sx={{ flexShrink: 0 }}
      >
        {options.map((option) => (
          <ToggleButton
            key={option.key}
            value={option.key}
            data-testid={`trainee-scheduling-${option.key}`}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            <Typography variant="caption" fontWeight={500} lineHeight={1.2}>
              {option.label}
            </Typography>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );
};

export default TraineeRequirementRow;
